import timesheetService, { Worker } from '../api/timesheetService';
import { fieldDb, FieldCacheRecord } from './fieldDb';

export const normalizeBadgeCode = (code: string): string => code.trim().toLowerCase();

export const workerMatchesBadge = (worker: Pick<Worker, 'badgeCode'>, badgeCode: string): boolean =>
  Boolean(worker.badgeCode && normalizeBadgeCode(worker.badgeCode) === normalizeBadgeCode(badgeCode));

const workerFromCacheRecord = (record: FieldCacheRecord): Worker | null => {
  const payload = record.payload as Partial<Worker> & { badge_code?: string | null };
  if (!payload.id || !payload.name) return null;
  return {
    id: String(payload.id),
    name: String(payload.name),
    badgeCode: payload.badgeCode ?? payload.badge_code ?? null,
    teamId: payload.teamId ?? null,
    trade: payload.trade ?? null,
    isActive: payload.isActive ?? true,
  };
};

const workerAllowedForTeams = (worker: Pick<Worker, 'teamId'>, teamIds?: string[]): boolean =>
  !teamIds?.length || Boolean(worker.teamId && teamIds.includes(worker.teamId));

export const findCachedWorkerByBadge = async (badgeCode: string, teamIds?: string[]): Promise<Worker | null> => {
  const normalized = normalizeBadgeCode(badgeCode);
  if (!normalized) return null;

  const cachedWorkers = await fieldDb.fieldCache.where('entityType').equals('worker').toArray();
  for (const record of cachedWorkers) {
    const worker = workerFromCacheRecord(record);
    if (worker && workerMatchesBadge(worker, normalized) && workerAllowedForTeams(worker, teamIds)) return worker;
  }
  return null;
};

export const resolveWorkerByBadge = async (
  badgeCode: string,
  loadedWorkers: Worker[],
  options: { teamIds?: string[] } = {}
): Promise<Worker | null> => {
  const normalized = normalizeBadgeCode(badgeCode);
  if (!normalized) return null;

  const loaded = loadedWorkers.find((worker) => workerMatchesBadge(worker, normalized) && workerAllowedForTeams(worker, options.teamIds));
  if (loaded) return loaded;

  const cached = await findCachedWorkerByBadge(normalized, options.teamIds);
  if (cached) return cached;

  if (options.teamIds?.length === 1) {
    const [remote] = await timesheetService.listWorkers({ badgeCode: badgeCode.trim(), active: true, teamId: options.teamIds[0] });
    return remote && workerAllowedForTeams(remote, options.teamIds) ? remote : null;
  }

  const [remote] = await timesheetService.listWorkers({ badgeCode: badgeCode.trim(), active: true });
  return remote && workerAllowedForTeams(remote, options.teamIds) ? remote : null;
};
